<template>
  <!-- Mobile - Horizontal Tool Bar (Figma 4356:83462 / 7671:240265).
       Outer wrapper 343×55; an inner row clips Frame 1223 (which holds the
       six 70.89×58 tool buttons with itemSpacing=-16) so the visible row is
       exactly 55px tall. -->
  <!-- The radius matches the card's own (--sheet-corner-radius): this wrap
       clips the gradient fades to the top corners, and the fades must stop
       exactly where the card's cutout starts, or grey shows through the
       fade on top of the corner. -->
  <div class="mobile-edit-toolbar-wrap relative w-full flex justify-center pointer-events-auto overflow-hidden rounded-t-[var(--sheet-corner-radius)]">
    <!-- Gradient Fades -->
    <div class="toolbar-fade toolbar-fade--left" />
    <div class="toolbar-fade toolbar-fade--right" />

    <div class="mobile-edit-toolbar overflow-x-auto no-scrollbar">
      <!-- Undo -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!undoStatus"
        aria-label="Undo"
        @click="undo"
      >
        <img src="/new-studio/mobile/tool-undo.svg" alt="" class="tool-icon" />
        <span class="tool-label">Undo</span>
      </button>

      <!-- Redo -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!redoStatus"
        aria-label="Redo"
        @click="redo"
      >
        <img src="/new-studio/mobile/tool-redo.svg" alt="" class="tool-icon" />
        <span class="tool-label">Redo</span>
      </button>

      <!-- Delete -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!deleteStatus"
        aria-label="Delete"
        @click="deleteSelected"
      >
        <img src="/new-studio/mobile/tool-delete.svg" alt="" class="tool-icon" />
        <span class="tool-label">Delete</span>
      </button>

      <!-- Copy -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!deleteStatus"
        aria-label="Copy"
        @click="copySelected"
      >
        <img src="/new-studio/mobile/tool-copy.svg" alt="" class="tool-icon" />
        <span class="tool-label">Copy</span>
      </button>

      <!-- Cut -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!deleteStatus"
        aria-label="Cut"
        @click="cutSelected"
      >
        <img src="/new-studio/mobile/tool-cut.svg" alt="" class="tool-icon" />
        <span class="tool-label">Cut</span>
      </button>

      <!-- Paste -->
      <button
        type="button"
        class="tool-pill-btn"
        :disabled="!hasClipboard"
        aria-label="Paste"
        @click="pasteSelected"
      >
        <img src="/new-studio/mobile/tool-paste.svg" alt="" class="tool-icon" />
        <span class="tool-label">Paste</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { executeMobilePaste } from './clipboardHelper'

const gridStore = useGridStore()
const projectStore = useProjectStore()
const runtimeConfig = useRuntimeConfig()

const undoStatus = computed(() => gridStore.undoStack.length > 0)
const redoStatus = computed(() => gridStore.redoStack.length > 0)
const deleteStatus = computed(() => gridStore.selectedItemsOnGrid.length > 0)
const hasClipboard = computed(() => (gridStore.clipboard?.items?.length || 0) > 0)

const undo = () => gridStore.undo()
const redo = () => gridStore.redo()

const deleteSelected = () => {
  gridStore.saveState()
  gridStore.deleteSelectedItemsOnGrid()
  if (runtimeConfig.public.autoSaveOnChange) {
    projectStore.save(true, true)
  }
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
}

const copySelected = () => {
  if (gridStore.selectedItemsOnGrid.length > 0) {
    gridStore.clipboard = {
      action: 'copy',
      items: gridStore.selectedItemsOnGrid.map(item => ({
        ...item,
        metadata: item.metadata ? { ...item.metadata } : undefined,
        attrs: item.attrs ? { ...item.attrs, metadata: item.attrs.metadata ? { ...item.attrs.metadata } : undefined } : undefined,
      })),
    }
    gridStore.setTempNewItems([])
  }
}

const cutSelected = () => {
  if (gridStore.selectedItemsOnGrid.length > 0) {
    gridStore.clipboard = {
      action: 'cut',
      items: gridStore.selectedItemsOnGrid.map(item => ({
        ...item,
        metadata: item.metadata ? { ...item.metadata } : undefined,
        attrs: item.attrs ? { ...item.attrs, metadata: item.attrs.metadata ? { ...item.attrs.metadata } : undefined } : undefined,
      })),
    }
    gridStore.setTempNewItems([])
    deleteSelected()
  }
}

const pasteSelected = () => {
  executeMobilePaste(gridStore, projectStore, runtimeConfig)
}
</script>

<style scoped>
/* === Figma 4356:83462 / 7671:240265 — Mobile Horizontal Tool Bar ===
   Figma root is 343×55 (Mobile - Tool bar 2 wrapper). Inside, Frame 1223
   holds six 70.89×58 buttons auto-laid out with itemSpacing: -16 (each
   button's 1.5px border overlaps the next by 16px, so the assembled row
   reads as a single continuous pillbar). Frame 1223 is 58 tall, slightly
   taller than the 55 wrapper — Figma clips via HorizontalBorder
   (clipsContent: true). We mirror that with a fixed-height wrapper. */
.mobile-edit-toolbar {
  display: flex;
  align-items: center;
  height: 55px;
  margin: 0 -8px;
  max-width: 100%;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Figma button: 70.89×58, padding T/B 8.06 L/R 3.22, itemSpacing 4.83.
   The Figma button has NO visible stroke, fill, or shadow — the icon and
   label sit on a transparent background. The buttons are visually
   separated only by the gap between them (which we keep at -16 so the
   1.5px transparent borders overlap cleanly without a visible seam). The
   wrapper's negative horizontal margins balance the row so it stays
   343px wide. */
.tool-pill-btn {
  width: 71px;
  height: 58px;
  padding: 8px 3px;
  margin-left: -16px;
  background: transparent;
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #1C1C1E;
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 600;
  transition: all 0.15s ease;
  touch-action: manipulation;
  flex-shrink: 0;
}

.tool-pill-btn:first-child {
  margin-left: 0;
}

.tool-pill-btn:active {
  transform: scale(0.96);
  opacity: 0.7;
}

.tool-pill-btn:disabled {
  opacity: 0.38;
  pointer-events: none;
}

/* Figma Undo vector: 21.75×21.75. Each SVG has aspect-ratio maintained
   inside the container; the explicit width/height keeps them crisp at the
   larger 22px target (vs the old 16px). */
.tool-icon {
  display: block;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

/* Figma label style: 10.07px Montserrat SemiBold, line-height 12.08px,
   color #1C1C1E, centered horizontally below the icon. */
.tool-label {
  display: block;
  font-size: 10px;
  line-height: 12px;
  color: #1C1C1E;
}

/* Figma 4356:83462 / 7671:240265 Gradient rectangles.
   Left fade is 28×54 at the left edge of the bar; right fade is 27×54
   mirrored. They mask content that scrolls out under the toolbar's edges.

   Figma draws these as opaque white because its toolbar sits on a white
   sheet. Ours does NOT — .mobile-tools-container is bg-transparent so the
   canvas shows through, and the sheet arrow sits directly above the bar.
   Fading to white therefore painted two near-opaque white bands that read
   as bars flanking the arrow. Fade to the sheet's own backdrop instead:
   transparent at the outer edge, toward the canvas at the inner edge. */
.toolbar-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
}

.toolbar-fade--left {
  left: 0;
  width: 28px;
  background: linear-gradient(to right, rgba(249, 249, 249, 0), rgba(249, 249, 249, 0.85));
}

.toolbar-fade--right {
  right: 0;
  width: 27px;
  background: linear-gradient(to left, rgba(249, 249, 249, 0), rgba(249, 249, 249, 0.85));
}
</style>
